import React, { useState } from 'react';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { useAuth } from '../contexts/AuthContext';
import { ErrorReportDialog } from './ErrorReportDialog';

const USER_ERRORS = new Set([
  'auth/email-already-in-use', 'auth/invalid-email', 'auth/weak-password',
  'auth/password-does-not-meet-requirements', 'auth/operation-not-allowed',
  'auth/popup-closed-by-user', 'auth/cancelled-popup-request', 'auth/popup-blocked',
]);

export const SignUp: React.FC = () => {
  // Brewery invitation links carry the invited address (`?invite=`). Membership
  // is granted by matching the *authenticated* email server-side, so this only
  // prefills the form — it stops people signing up with the wrong address and
  // silently not joining the brewery.
  const [searchParams] = useSearchParams();
  const invitedEmail = searchParams.get('invite') || '';
  const [email, setEmail] = useState(invitedEmail);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [reportError, setReportError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const { signUp, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }

    try {
      setError(null);
      setLoading(true);
      await signUp(email, password);
      navigate('/settings');
    } catch (error: any) {
      console.error('Error signing up:', error);
      let errorMessage = 'Failed to create an account. Please try again.';

      if (error?.code) {
        switch (error.code) {
          case 'auth/email-already-in-use':
            errorMessage = 'An account with this email already exists.';
            break;
          case 'auth/invalid-email':
            errorMessage = 'Please enter a valid email address.';
            break;
          case 'auth/weak-password':
            errorMessage = 'Password is too weak. Please use at least 6 characters.';
            break;
          case 'auth/password-does-not-meet-requirements':
            // Extract requirements from Firebase message
            const match = error.message?.match(/Missing password requirements: \[(.+?)\]/);
            errorMessage = match ? match[1] : 'Password does not meet requirements. Include uppercase, lowercase, number, and special character.';
            break;
          case 'auth/operation-not-allowed':
            errorMessage = 'Email/password sign-up is not enabled. Please contact support.';
            break;
          default:
            errorMessage = error.message || errorMessage;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      if (!USER_ERRORS.has((error as any)?.code)) setReportError(error);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setError(null);
      setLoading(true);
      await signInWithGoogle();
      navigate('/settings');
    } catch (error: any) {
      console.error('Error signing in with Google:', error);
      setLoading(false);

      let errorMessage = 'Failed to sign in with Google.';

      if (error?.code) {
        switch (error.code) {
          case 'auth/popup-closed-by-user':
            errorMessage = 'Sign-in popup was closed. Please try again.';
            break;
          case 'auth/popup-blocked':
            errorMessage = 'Sign-in popup was blocked. Please allow popups for this site and try again.';
            break;
          case 'auth/cancelled-popup-request':
            // User closed the popup, don't show error
            setLoading(false);
            return;
          case 'auth/app-check-token-invalid':
            errorMessage = 'Security verification failed. Please refresh the page and try again.';
            break;
          case 'auth/operation-not-allowed':
            errorMessage = 'Google sign-in is not enabled. Please contact support.';
            break;
          default:
            errorMessage = `Error: ${error.code} - ${error.message}`;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      if (!USER_ERRORS.has((error as any)?.code)) setReportError(error);
      setError(errorMessage);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 2 }}>
      <ErrorReportDialog
        open={reportError !== null}
        error={reportError}
        onClose={() => setReportError(null)}
      />
      <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%' }}>
        <Typography variant="h5" component="h1" gutterBottom align="center">
          Create Account
        </Typography>

        {invitedEmail && (
          <Alert severity="info" sx={{ mt: 2 }}>
            You've been invited to join a brewery on Batchfather. Sign up with{' '}
            <strong>{invitedEmail}</strong> to accept — you'll be added automatically.
          </Alert>
        )}

        {/* Google Sign-In Button */}
        <Button
          fullWidth
          variant="outlined"
          startIcon={<GoogleIcon />}
          onClick={handleGoogleSignIn}
          disabled={loading}
          sx={{ mt: 2 }}
        >
          Continue with Google
        </Button>

        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary">
            OR
          </Typography>
        </Divider>

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            required
            autoComplete="email"
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
            autoComplete="new-password"
          />
          <TextField
            fullWidth
            label="Confirm Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="normal"
            required
            autoComplete="new-password"
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}
              action={reportError ? (
                <Button color="inherit" size="small" onClick={() => setReportError(reportError)}>
                  Report
                </Button>
              ) : undefined}
            >
              {error}
            </Alert>
          )}
          <Button
            fullWidth
            variant="contained"
            color="primary"
            type="submit"
            disabled={loading}
            sx={{ mt: 3 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Sign Up'}
          </Button>
        </form>

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Already have an account?
          </Typography>
          <Button
            component={RouterLink}
            to={invitedEmail ? `/login?invite=${encodeURIComponent(invitedEmail)}` : '/login'}
            variant="outlined"
            fullWidth
          >
            Sign In
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}; 